import React, { useEffect, useState } from "react";
import moment from "moment";
import { withRouter } from "react-router-dom";
import { Popover, OverlayTrigger } from "react-bootstrap";
import { debounce } from "throttle-debounce";
import { Button, Icon } from "aible";
import { LineInput, CheckboxInput } from "components/Inputs/LineInput";
import RangeSlider from "components/Inputs/Slider/RangeSlider";
import Tooltip from "components/Inputs/Tooltip";
import SearchInput from "components/Inputs/SearchInput/SearchInput";
import { updateDatasetId, getCurrentNextId } from "@/containers/Next/NextReducer";
import { updateIsCSVUpload } from "@/containers/ChatGPT/reducer";
import { createSchemaMonitor, DATA_SOURCES, DEFAULT_DATA_SOURCE, deleteSchemaMonitor, fetchSchemaMonitorCredentials, findDataSourceDetails, generateOutcomeRecommendations, generateTransformRecommendations, getIsShowModal, getSchemaMonitor, getSchemaMonitorLinkTableList, getSchemaMonitorSelectedTable, getSchemaMonitorsOptions, getSchemaTables, getSelectedSchemaMonitor, getSelectedSchemaMonitorLinkTableList, getTransformRecommendations, linkSelectedSchemaMonitorTables, refreshSchemaMonitor, removeOutcomeRecommendations, resetTransformRecommendations, selectSchemaMonitor, selectTableInSelectedMonitor, toggleSelectedSchemaLinkTable, updateIsShowModal, updateSchemaLinkTableOutcome, updateSchemaMonitorLinkTableTransformation, updateSelectedSchemaLinkTableList, updateSelectedSchemaMonitorAuth, filterSchemaMonitorSettings, handleOAuthAuthorization, getIsSafeImportMode, toggleSafeModeImport, fetchSchemaMonitor } from "containers/SchemaMonitor/reducer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faLock, faFile, faInfo } from "@fortawesome/free-solid-svg-icons";
import { ACCOUNT_LOGOS, getAvailableAccountTypes, getSelectedAccount, updateSelectedAccountToFirstOfTypes, getAccount } from "containers/Accounts/reducer";
import { isEnabled } from "utils/featureFlags";
import { getStringComparator } from "utils/collect";
import AccountSelectorContainer from "containers/Accounts/AccountSelector";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import LabeledToggle from "components/Inputs/Toggle/Toggle";
import Select from "react-select";
import { selectOnChangeWrapper, selectOnChangeWrapperIfPresent } from "utils/select";
import { useDispatch, useSelector } from "types/store";
import styles from "./style.module.css";
import { getFolder, getSelectedFolder } from "containers/Folders/reducer";
import { getIsSchemaMonitorLoading } from "containers/App/reducer";
import { getDataset } from "containers/Home/reducer";
import { toast } from "react-toastify";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/aible/Dialog/Dialog";
import { AutoSizer, Column, Table } from "react-virtualized";
const SCHEMA_MONITOR_STEPS = {
    LOADING: "Loading...",
    SELECT_MONITOR: "Select Database Connection",
    CREATE_MONITOR: "Create New Database Connection",
    EXPLORE_MONITOR: "Select Table"
};
const SchemaMonitorModal = ({ selectDataOnLink }) => {
    const dispatch = useDispatch();
    const showModal = useSelector((state) => getIsShowModal(state));
    const selectedMonitor = useSelector((state) => getSelectedSchemaMonitor(state));
    const isLoading = useSelector((state) => getIsSchemaMonitorLoading(state));
    const schemaMonitorOptions = useSelector((state) => getSchemaMonitorsOptions(state));
    const linkTableList = useSelector((state) => getSelectedSchemaMonitorLinkTableList(state));
    const selectedFolder = useSelector((state) => getFolder(state, getSelectedFolder(state)));
    const selectedMonitorAccount = useSelector((state) => getSelectedAccount(state));
    const activeMonitorAccount = useSelector((state) => getAccount(state, selectedMonitor?.account_id));
    const nextId = useSelector((state) => getCurrentNextId(state));
    const [settings, updateSettings] = useState({});
    const [isGloballyAvailable, updateIsGloballyAvailable] = useState(false);
    const [showUpdateCredentials, updateShowUpdateCredentials] = useState(false);
    const [isImportingDatasets, updateIsImportingDatasets] = useState(false);
    const [isCreating, updateIsCreating] = useState();
    const [isFetchingCredentials, updateIsFetchingCredentials] = useState(false);
    const fetchCredentials = (id) => {
        updateIsFetchingCredentials(true);
        dispatch(fetchSchemaMonitorCredentials(id))
            .then((credentials) => {
            if (credentials) {
                updateSettings(credentials);
            }
        })
            .finally(() => {
            updateIsFetchingCredentials(false);
            return Promise.resolve();
        });
    };
    useEffect(() => {
        if (selectedMonitor) {
            fetchCredentials(selectedMonitor.id);
            updateIsCreating(undefined);
        }
        updateShowUpdateCredentials(false);
    }, [selectedMonitor?.id, selectedMonitor?.status]);
    const linkMonitors = () => {
        updateIsImportingDatasets(true);
        return dispatch(linkSelectedSchemaMonitorTables())
            .then((response) => {
            if (selectDataOnLink) {
                if (response?.length) {
                    dispatch(updateDatasetId(nextId, response?.[0]));
                    dispatch(updateIsCSVUpload(false));
                }
            }
        })
            .finally(() => {
            updateIsImportingDatasets(false);
            return dispatch(updateIsShowModal(false));
        });
    };
    const updateMonitorAuth = (forceRefresh = false) => {
        const source = DATA_SOURCES.find((s) => s?.value === selectedMonitor?.type);
        const cleanedSettings = filterSchemaMonitorSettings(settings, source);
        dispatch(updateSelectedSchemaMonitorAuth(cleanedSettings, forceRefresh));
        updateShowUpdateCredentials(false);
    };
    const updateSetting = (key, setting) => updateSettings({ ...settings, [key]: setting });
    const createMonitor = () => {
        const cleanedSettings = filterSchemaMonitorSettings(settings, isCreating);
        const folderId = isGloballyAvailable ? undefined : selectedFolder?.id;
        dispatch(createSchemaMonitor(cleanedSettings, folderId));
    };
    const handleRefresh = () => {
        const refreshId = selectedMonitor?.id;
        if (!refreshId) {
            return;
        }
        return dispatch(refreshSchemaMonitor(refreshId));
    };
    const handleSelect = (v) => {
        if (!v) {
            return dispatch(selectSchemaMonitor(undefined));
        }
        if (v == -1) {
            handleUpdateIsCreating();
            return dispatch(selectSchemaMonitor(undefined));
        }
        else {
            updateIsCreating(undefined);
            return dispatch(selectSchemaMonitor(v));
        }
    };
    const handleUpdateIsCreating = (value) => {
        const source = value ??
            DATA_SOURCES.find((s) => !s.hide &&
                (selectedMonitorAccount === undefined ||
                    s.allowedAccounts === undefined ||
                    s.allowedAccounts?.includes(selectedMonitorAccount.type))) ??
            DEFAULT_DATA_SOURCE;
        const settings = source.settings.reduce((s, x) => {
            s[x.value] = x.default ?? "";
            return s;
        }, {});
        settings.type = source.value;
        if (source.allowedAccounts?.length) {
            const vAllowedAccounts = source.allowedAccounts;
            if (selectedMonitorAccount?.type && !vAllowedAccounts.includes(selectedMonitorAccount?.type)) {
                dispatch(updateSelectedAccountToFirstOfTypes(vAllowedAccounts));
            }
        }
        updateIsCreating(source);
        return updateSettings(settings);
    };
    const accountType = activeMonitorAccount?.type ?? "GCP";
    const currStep = isFetchingCredentials
        ? SCHEMA_MONITOR_STEPS.LOADING
        : isCreating
            ? SCHEMA_MONITOR_STEPS.CREATE_MONITOR
            : !selectedMonitor
                ? SCHEMA_MONITOR_STEPS.SELECT_MONITOR
                : SCHEMA_MONITOR_STEPS.EXPLORE_MONITOR;
    const isSpinning = selectedMonitor?.status == "WAITING_FOR_SCHEMA" || isFetchingCredentials;
    const requiresOAuthAuthorization = selectedMonitor?.oAuthUrl && selectedMonitor.status === "MISSING_CREDENTIALS_URL";
    return (React.createElement(Dialog, { open: showModal, onOpenChange: () => dispatch(updateIsShowModal(false)) },
        React.createElement(DialogContent, { size: "xl" },
            React.createElement("div", { id: "schema-monitor-modal" }),
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, currStep === SCHEMA_MONITOR_STEPS.CREATE_MONITOR || selectedMonitor === undefined
                    ? "Create" + (isCreating?.label ? " " + isCreating.label : "") + " Database Connection"
                    : currStep === SCHEMA_MONITOR_STEPS.EXPLORE_MONITOR
                        ? showUpdateCredentials
                            ? "Update Credentials for " + selectedMonitor.name
                            : "Select Table in " + selectedMonitor.name
                        : currStep)),
            React.createElement("div", { className: styles.modalBody }, isLoading ? (React.createElement("div", { className: styles.loadingIndicator },
                React.createElement(LoadingIndicator, null))) : (React.createElement(React.Fragment, null,
                React.createElement("div", { style: { height: "100%", display: "flex", flexDirection: "column" } },
                    React.createElement("div", { className: styles.selectMonitorInput },
                        React.createElement("div", { className: styles.selectMonitorTitle }, "Database Connection:"),
                        React.createElement("div", { style: { flexGrow: 1 } },
                            React.createElement(Select, { options: schemaMonitorOptions, value: selectedMonitor
                                    ? {
                                        value: selectedMonitor.id,
                                        label: selectedMonitor.name
                                    }
                                    : isCreating
                                        ? {
                                            value: -1,
                                            label: "Create New Database Connection"
                                        }
                                        : null, placeholder: "Select Database Connection", onChange: selectOnChangeWrapper(handleSelect), isClearable: true })),
                        React.createElement("div", { className: styles.selectMonitorAction },
                            React.createElement(Button, { type: "secondary", onClick: () => handleSelect(-1) },
                                React.createElement(Icon, { type: "plus", size: 13, style: { marginRight: "6px" } }),
                                " ",
                                "Create New Connection"),
                            Boolean(selectedMonitor) && (React.createElement(React.Fragment, null,
                                React.createElement(OverlayTrigger, { placement: "top", trigger: ["hover"], rootClose: true, overlay: React.createElement(Popover, { id: "popover-positioned-top" }, "Delete Database Connection") },
                                    React.createElement(Button, { type: "secondary", style: {
                                            padding: "9px",
                                            marginLeft: "10px",
                                            width: "38px",
                                            height: "38px"
                                        }, onClick: () => selectedMonitor && dispatch(deleteSchemaMonitor(selectedMonitor.id)), disabled: !selectedMonitor?.permissions?.can_delete },
                                        React.createElement(Icon, { type: "trash", size: 13 }))),
                                React.createElement(OverlayTrigger, { placement: "top", trigger: ["hover"], rootClose: true, overlay: React.createElement(Popover, { id: "popover-positioned-top" },
                                        React.createElement("div", null,
                                            React.createElement("div", null, "Check Connection for New Tables"),
                                            React.createElement("div", { style: { marginTop: "8px" } }, "This may take several minutes to complete"),
                                            React.createElement("div", null, "Last updated: " + moment.utc(selectedMonitor?.updated_date).local().fromNow()))) },
                                    React.createElement(Button, { type: "secondary", style: {
                                            padding: "9px",
                                            marginLeft: "10px",
                                            position: "relative",
                                            width: "38px",
                                            height: "38px"
                                        }, onClick: () => handleRefresh(), disabled: isSpinning }, isSpinning ? (React.createElement(LoadingIndicator, { color: "#333", style: {
                                            height: "13px",
                                            width: "13px",
                                            margin: "0px",
                                            position: "absolute",
                                            transform: "scale(0.4)",
                                            top: "1px",
                                            left: "2px"
                                        } })) : (React.createElement(Icon, { type: "refresh", size: 13 })))))))),
                    currStep === SCHEMA_MONITOR_STEPS.CREATE_MONITOR ? (React.createElement(CreateSchemaMonitor, { isCreating: isCreating, updateIsCreating: (v) => handleUpdateIsCreating(v), settings: settings, updateSetting: updateSetting, isGloballyAvailable: isGloballyAvailable, updateIsGloballyAvailable: updateIsGloballyAvailable })) : currStep === SCHEMA_MONITOR_STEPS.SELECT_MONITOR ||
                        currStep === SCHEMA_MONITOR_STEPS.EXPLORE_MONITOR ? (React.createElement(SelectSchemaMonitor, { monitorId: selectedMonitor?.id, settings: settings, updateSetting: updateSetting, showUpdateCredentials: showUpdateCredentials, toggleUpdateCredentials: (x) => {
                            const update = x ?? !showUpdateCredentials;
                            updateShowUpdateCredentials(update);
                        }, updateCredentials: () => updateMonitorAuth(true), isImportingDatasets: isImportingDatasets })) : (React.createElement(LoadingIndicator, null)))))),
            React.createElement(DialogFooter, null,
                React.createElement(Button, { type: "secondary", onClick: () => dispatch(updateIsShowModal(false)) }, "Cancel"),
                !isLoading && (React.createElement(React.Fragment, null,
                    selectedMonitor &&
                        (selectedMonitor.status === "AUTHENTICATION_FAILURE" ||
                            selectedMonitor.status === "MISSING_CREDENTIALS_URL") && (React.createElement(Button, { type: "primary", onClick: () => updateMonitorAuth(true), disabled: requiresOAuthAuthorization === true }, "Update Database Connection")),
                    currStep === SCHEMA_MONITOR_STEPS.EXPLORE_MONITOR &&
                        !showUpdateCredentials &&
                        !(selectedMonitor &&
                            (selectedMonitor.status === "AUTHENTICATION_FAILURE" ||
                                selectedMonitor.status === "MISSING_CREDENTIALS_URL" ||
                                selectedMonitor.status === "WAITING_FOR_SCHEMA")) && (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: styles.footerInfoWrapper },
                            React.createElement("div", { className: styles.modalAccount },
                                React.createElement("span", { className: styles.modalFooterText }, `${linkTableList?.length !== 1 ? "These tables" : "This table"} will be added to your `),
                                React.createElement("span", { className: styles.targetHighlight },
                                    React.createElement(Icon, { src: ACCOUNT_LOGOS[accountType], alt: accountType }),
                                    React.createElement("span", { style: { marginLeft: "3px" } }, activeMonitorAccount?.name)),
                                React.createElement("span", { className: styles.modalFooterText }, `cloud account and be visible in the `),
                                React.createElement("span", { className: styles.targetHighlight }, selectedFolder?.name),
                                React.createElement("span", { className: styles.modalFooterText }, ` folder`)),
                            React.createElement("div", { className: styles.modalAccount },
                                React.createElement("span", { className: styles.modalFooterText }, `${selectedFolder?.roles?.length} ${selectedFolder?.roles?.length === 1 ? "user" : "users"} in this folder will have access to this data`))),
                        React.createElement(Button, { type: "primary", onClick: () => linkMonitors(), disabled: !selectedMonitor || !linkTableList?.length || !selectedMonitor?.permissions?.can_edit, style: { minWidth: "185px" } }, `Link to ${linkTableList?.length} Selected Table${linkTableList?.length !== 1 ? "s" : ""}`))),
                    currStep === SCHEMA_MONITOR_STEPS.CREATE_MONITOR && (React.createElement("div", { style: { display: "flex" } },
                        isGloballyAvailable ? (React.createElement("div", { className: styles.footerInfoWrapper },
                            React.createElement("div", { className: styles.modalAccount },
                                React.createElement("span", { className: styles.modalFooterText }, "This connection will be available for all folders and users in your organization")),
                            React.createElement("div", { className: styles.modalAccount },
                                React.createElement("span", { className: styles.modalFooterText }, "Please ensure that your users are allowed to view the data based on your data security model")))) : (React.createElement("div", { className: styles.footerInfoWrapper },
                            React.createElement("div", { className: styles.modalAccount },
                                React.createElement("span", { className: styles.modalFooterText }, `This connection will be added to the current folder: `),
                                React.createElement("span", { className: styles.targetHighlight }, selectedFolder?.name)),
                            React.createElement("div", { className: styles.modalAccount },
                                React.createElement("span", { className: styles.modalFooterText }, `${selectedFolder?.roles?.length} ${selectedFolder?.roles?.length === 1 ? "user" : "users"} in this folder will have access to this data`)))),
                        React.createElement("div", null,
                            React.createElement(Button, { type: "primary", onClick: () => createMonitor(), disabled: !selectedFolder?.permissions?.can_edit || (isCreating && settings?.name?.length < 1) }, "Create Database Connection"))))))))));
};
export const JsonFileInput = ({ onChange, label }) => {
    const [fileName, updateFileName] = useState();
    return (React.createElement("div", null,
        React.createElement("div", { style: { display: "none" } },
            React.createElement("input", { type: "file", id: "fCredUpload", onChange: (event) => {
                    const file = event.currentTarget.files ? event.currentTarget.files[0] : undefined;
                    if (file !== undefined) {
                        const fr = new FileReader();
                        fr.onload = () => {
                            if (typeof fr.result === "string") {
                                onChange(fr.result);
                                updateFileName(file.name);
                            }
                        };
                        fr.readAsText(file);
                    }
                } })),
        React.createElement(Button, { type: "secondary", onClick: () => document.getElementById("fCredUpload")?.click() },
            React.createElement(FontAwesomeIcon, { icon: faLock }),
            React.createElement("div", { style: { marginLeft: "5px" } }, label)),
        fileName !== undefined && (React.createElement("div", { style: { marginTop: "10px", display: "flex", alignItems: "center" } },
            React.createElement(FontAwesomeIcon, { icon: faFile }),
            React.createElement("div", { style: { marginLeft: "5px" } }, fileName)))));
};
const CreateSchemaMonitor = ({ isCreating, updateIsCreating, settings, updateSetting, isGloballyAvailable, updateIsGloballyAvailable }) => {
    const availableAccountTypes = useSelector((state) => getAvailableAccountTypes(state));
    if (!isCreating) {
        return React.createElement(React.Fragment, null);
    }
    const connectionAccessOptions = [
        {
            value: "private",
            label: "Private - Only available to users in this folder"
        },
        {
            value: "global",
            label: "Public - Available to all users in your organization"
        }
    ];
    const account = useSelector((state) => getSelectedAccount(state));
    const cloudBaseUrl = account?.configuration?.cloud_base_url ??
        (account.type === "AWS" ? account?.configuration?.lambda_api_endpoint : "");
    return (React.createElement("div", { className: styles.contentWrapper },
        React.createElement("div", { className: styles.createSchemaSelectSource }, DATA_SOURCES
            .filter((s) => availableAccountTypes.some((t) => s.allowedAccounts === undefined || s.allowedAccounts.includes(t)))
            .map((source) => {
            if (source.hide) {
                return React.createElement(React.Fragment, { key: "source-tile-" + source.value });
            }
            else {
                return (React.createElement(CreateSchemaTile, { key: "source-tile-" + source.value, isActive: isCreating && isCreating.value === source.value, data: source, onClick: () => updateIsCreating(source) }));
            }
        })),
        React.createElement("div", { className: styles.createSchemaSelectSettings },
            React.createElement("div", { className: styles.sourceLabel },
                React.createElement("span", { style: { minWidth: "100px", marginRight: "10px" } }, "Store Data in: "),
                React.createElement(AccountSelectorContainer, { removeAccountTypes: isCreating?.allowedAccounts })),
            isCreating.settings &&
                isCreating.settings
                    .filter((s) => !s.hide && !s.hideFunc?.(settings))
                    .map((s) => (React.createElement(SettingItemInput, { key: s.value, item: s, settings: settings, updateSetting: updateSetting, cloudBaseUrl: cloudBaseUrl }))),
            React.createElement("div", { className: styles.sourceLabel, style: { marginTop: "5px", marginBottom: "5px" } },
                React.createElement("span", { style: { minWidth: "100px", marginRight: "10px" } }, "Connection Access: "),
                React.createElement("span", { style: { width: "350px" } },
                    React.createElement(Select, { styles: {
                            menuList: (provided, _) => ({
                                ...provided,
                                maxHeight: "200px"
                            })
                        }, options: connectionAccessOptions, value: connectionAccessOptions[isGloballyAvailable ? 1 : 0], onChange: selectOnChangeWrapper((val) => updateIsGloballyAvailable(val == "global")), menuPlacement: "top" }))))));
};
const SettingItemInput = ({ settings, updateSetting, item, forUpdate, cloudBaseUrl }) => {
    if (item.isJsonUpload) {
        return (React.createElement("div", { style: { marginLeft: "185px", marginTop: "10px" } },
            React.createElement(JsonFileInput, { key: "setting-input" + item.value, label: item.label, onChange: (fileText) => updateSetting(item.value, JSON.parse(fileText)) })));
    }
    else if (item.allowedValues) {
        return (React.createElement("div", { className: styles.sourceLabel, style: { marginTop: "5px", paddingBottom: "5px" } },
            React.createElement("span", { style: { minWidth: "100px", marginRight: "10px" } }, item.label + ":"),
            React.createElement("span", { style: { width: "350px" } },
                React.createElement(Select, { styles: {
                        menuList: (provided, _) => ({
                            ...provided,
                            maxHeight: "200px"
                        })
                    }, options: item.allowedValues, value: item.allowedValues.find((x) => x.value == settings?.[item.value]), onChange: selectOnChangeWrapper((val) => val && updateSetting(item.value, val)), menuPlacement: "top" }))));
    }
    else if (item.isCheckbox) {
        const val = settings?.[item.value];
        return (React.createElement("div", { style: { display: "flex", alignItems: "center" } },
            React.createElement(CheckboxInput, { key: "setting-input" + item.value, isMediumBox: true, value: (typeof val === "boolean" ? val : false) ?? false, onChange: (val) => updateSetting(item.value, val), label: item.label, labelStyle: {
                    width: "175px",
                    textAlign: "right",
                    flexShrink: 0
                }, isPassword: item.isPassword, isCheckbox: item.isCheckbox, placeholder: item.isPassword && forUpdate ? "********" : item.isOptional ? "Optional" : undefined, updateInstant: true }),
            cloudBaseUrl && item?.showCheckboxCloudbaseUrl && val === item?.showCheckboxCloudbaseUrl?.showCondition && (React.createElement(React.Fragment, null,
                item.showCheckboxCloudbaseUrl?.label && (React.createElement("span", { style: item.showCheckboxCloudbaseUrl?.labelStyle }, item.showCheckboxCloudbaseUrl?.label)),
                React.createElement("span", { onClick: (e) => {
                        navigator.clipboard.writeText(e.currentTarget.innerText);
                        toast("URL copied to clipboard!", {
                            type: toast.TYPE.SUCCESS,
                            position: toast.POSITION.TOP_CENTER
                        });
                    }, style: item.showCheckboxCloudbaseUrl?.urlStyle }, cloudBaseUrl + item.showCheckboxCloudbaseUrl?.url)))));
    }
    else {
        return (React.createElement(LineInput, { key: "setting-input" + item.value, isMediumBox: true, value: settings?.[item.value]?.toString() ?? "", onChange: (val) => updateSetting(item.value, val), label: item.label, labelStyle: {
                width: "175px",
                textAlign: "right",
                flexShrink: 0
            }, isPassword: item.isPassword, isCheckbox: item.isCheckbox, placeholder: item.isPassword && forUpdate ? "********" : item.isOptional ? "Optional" : undefined, updateInstant: true }));
    }
};
const CreateSchemaTile = ({ data, isActive, onClick }) => {
    return (React.createElement("div", { className: styles.schemaTile + " " + (isActive ? styles.active : ""), onClick: onClick },
        React.createElement("div", { className: styles.dataSourceIcon },
            React.createElement("img", { src: data.image })),
        React.createElement("div", { className: styles.dataSourceName }, data.label)));
};
const SelectSchemaMonitor = ({ monitorId, settings, updateSetting, toggleUpdateCredentials, updateCredentials, isImportingDatasets, showUpdateCredentials }) => {
    const selectedMonitor = useSelector((state) => monitorId === undefined ? monitorId : getSchemaMonitor(state, monitorId));
    const dispatch = useDispatch();
    const account = useSelector((state) => getSelectedAccount(state));
    const [isLoading, setIsLoading] = useState(selectedMonitor?.schema === undefined);
    useEffect(() => {
        if (monitorId !== undefined && selectedMonitor !== undefined && selectedMonitor?.schema === undefined) {
            setIsLoading(true);
            dispatch(fetchSchemaMonitor(monitorId)).finally(() => setIsLoading(false));
        }
    }, [monitorId]);
    if (!selectedMonitor) {
        return (React.createElement("div", { className: styles.contentWrapper + " " + styles.loadingIndicator },
            React.createElement("div", { className: styles.modalWrapper },
                React.createElement("div", { className: styles.modalInfoTitle },
                    React.createElement("div", { className: styles.modalInfoIcon },
                        React.createElement(FontAwesomeIcon, { icon: faInfo })),
                    "Your data security is important"),
                React.createElement("div", { className: styles.modalInfoBody }, "A database connection you add is only available to the folder you have selected."),
                React.createElement("div", { className: styles.modalInfoBody }, "Any user you add to this folder can view any dataset it contains. Please ensure that any user added to this folder is allowed to view the data based on your data security model."),
                React.createElement("div", { className: styles.modalInfoBody }, "If you have users with different roles, please create a folder for each role and create a database connection with appropriate credentials for that role. This ensures that users only have access to AI that are only aware of the data that the users have access to."))));
    }
    const selectedMonitorDetails = selectedMonitor.type
        ? findDataSourceDetails(selectedMonitor.type)
        : undefined;
    const monitorStatus = selectedMonitor.status;
    const monitorAnnotations = selectedMonitor?.annotations;
    let errorMessage = undefined;
    if (monitorAnnotations?.length > 0) {
        for (let i = monitorAnnotations?.length - 1; i >= 0; i--) {
            if (monitorAnnotations?.[i]?.type === "error") {
                errorMessage = monitorAnnotations?.[i]?.message + " " + monitorAnnotations?.[i]?.created_date;
                break;
            }
        }
    }
    const requiresOAuthAuthorization = selectedMonitor?.oAuthUrl && selectedMonitor?.requiresOAuth;
    const cloudBaseUrl = account?.configuration?.cloud_base_url ?? account?.configuration?.lambda_api_endpoint;
    return monitorStatus === "AUTHENTICATION_FAILURE" ||
        monitorStatus === "MISSING_CREDENTIALS_URL" ||
        requiresOAuthAuthorization ? (React.createElement(React.Fragment, null, requiresOAuthAuthorization ? (React.createElement("div", { className: styles.oAuthModalContent },
        React.createElement("div", { className: styles.oAuthModalHeader }, "You're almost done!"),
        React.createElement("div", { className: styles.oAuthModalText }, "Click the button below to open a popup for authorizing with OAuth 2.0"),
        React.createElement("div", { className: styles.oAuthModalText }, "Please wait a moment after closing the popup for your connection to appear"),
        React.createElement(Button, { onClick: () => {
                if (selectedMonitor?.oAuthUrl) {
                    dispatch(handleOAuthAuthorization(selectedMonitor.id, selectedMonitor.account_id, selectedMonitor.oAuthUrl));
                }
            }, isLoading: selectedMonitor.isAttemptingOAuth === true, loadingText: "Please complete authorizing in the popup..." }, "Authorize With OAuth 2.0"))) : (React.createElement("div", { className: styles.contentWrapper },
        React.createElement("div", { className: styles.tableListTitle }, "There was an error connecting the Database Connection" +
            (selectedMonitorDetails ? " to " + selectedMonitorDetails.label : "") +
            ". Please try again."),
        errorMessage && React.createElement("div", { className: styles.errorMessageText }, errorMessage),
        React.createElement("div", { className: styles.createSchemaSelectSettings }, (selectedMonitorDetails?.settings ?? [])
            .filter((s) => s.value !== "name" && !s.hideFunc?.(settings))
            .map((s) => (React.createElement(SettingItemInput, { key: s.value, item: s, settings: settings, updateSetting: updateSetting, cloudBaseUrl: cloudBaseUrl, forUpdate: true })))))))) : isLoading ? (React.createElement("div", null,
        React.createElement("div", { className: styles.contentWrapper + " " + styles.loadingIndicator },
            React.createElement(LoadingIndicator, null),
            React.createElement("div", null, "Loading Connection Tables")))) : monitorStatus === "WAITING_FOR_SCHEMA" ? (React.createElement("div", null,
        React.createElement("div", { className: styles.contentWrapper + " " + styles.loadingIndicator },
            React.createElement(LoadingIndicator, null),
            React.createElement("div", null, "Establishing Database Connection"),
            React.createElement("div", { style: { fontSize: "13px" } }, "This will only take a minute")))) : isImportingDatasets ? (React.createElement("div", null,
        React.createElement("div", { className: styles.contentWrapper + " " + styles.loadingIndicator },
            React.createElement(LoadingIndicator, null),
            React.createElement("div", null, `Starting data quality check (${selectedMonitor.uploadCount ?? 0} of ${selectedMonitor?.linkTableList?.length ?? 0})`),
            React.createElement("div", { style: { marginTop: "10px" } }, "Please do not close this window")))) : (React.createElement("div", { className: styles.contentWrapper },
        React.createElement("div", { className: styles.monitorDetails },
            selectedMonitorDetails ? (React.createElement("div", { className: styles.monitorDetailsItem },
                React.createElement(Icon, { src: selectedMonitorDetails.image, size: 16, style: { marginRight: "6px" } }),
                React.createElement("span", null, selectedMonitorDetails.label + " Database Connection"))) : (""),
            selectedMonitor.created_by ? (React.createElement("div", { className: styles.monitorDetailsItem },
                React.createElement("span", { style: { marginRight: "4px" } }, "Created by: "),
                React.createElement("span", null, selectedMonitor.created_by.fullname))) : (""),
            React.createElement("div", { className: styles.monitorDetailsItem }, moment.utc(selectedMonitor.created_date).local().format("MMMM Do YYYY, h:mm:ss a")),
            React.createElement("div", { className: styles.monitorDetailsItem }, isEnabled("schema-monitor-update-credentials") && !showUpdateCredentials && (React.createElement(Tooltip, { overlay: showUpdateCredentials ? "Back to Table Select" : "Update Database Connection", trigger: [] },
                React.createElement(Button, { onClick: () => toggleUpdateCredentials(), style: { minWidth: "230px" }, disabled: !selectedMonitor?.permissions?.can_edit },
                    !showUpdateCredentials && (React.createElement("span", { style: { marginRight: "6px" } },
                        React.createElement(FontAwesomeIcon, { className: "fa-w-16", icon: faEdit }))),
                    React.createElement("span", null, showUpdateCredentials ? "Back to Table Select" : "Update Database Connection")))))),
        showUpdateCredentials ? (React.createElement("div", { className: styles.contentWrapper },
            React.createElement("div", { className: styles.tablesTitle }, "Please enter your new database connection credentials."),
            React.createElement("div", { className: styles.updateSchemaMonitorWarning, style: { textAlign: "left", margin: "0px 0px 0px 10px" } }, "It may take a few minutes for your data to update"),
            React.createElement("div", { className: styles.updateSchemaMonitorSettings }, (selectedMonitorDetails?.settings ?? [])
                .filter((s) => s.value !== "name" && !s.hideFunc?.(settings))
                .map((s) => (React.createElement(SettingItemInput, { key: s.value, item: s, settings: settings, updateSetting: updateSetting, forUpdate: true })))),
            React.createElement("div", { className: styles.updateSchemaMonitorWarning }, "THIS WILL OVERWRITE YOUR CURRENT SETTINGS"),
            React.createElement("div", { className: styles.updateSchemaMonitorButtons },
                React.createElement(Button, { type: "secondary", style: { marginLeft: "10px", marginRight: "auto" }, onClick: () => toggleUpdateCredentials(false) }, "Back to Table Select"),
                React.createElement(Button, { type: "primary", onClick: () => updateCredentials(), disabled: !selectedMonitor?.permissions?.can_edit }, "Update Database Connection")))) : (React.createElement(React.Fragment, null,
            React.createElement("div", { className: styles.tablesTitle }, "Select tables to import to your cloud account"),
            React.createElement("div", { className: styles.tablesWrapper },
                React.createElement(SchemaMonitorTables, { monitorId: selectedMonitor.id }),
                React.createElement(SchemaMonitorTableDetails, { monitorId: selectedMonitor.id, noClick: true }))))));
};
const SchemaMonitorTableChecked = ({ monitorId, table }) => {
    const dispatch = useDispatch();
    const monitor = useSelector((state) => getSchemaMonitor(state, monitorId));
    const isInLinkTableList = monitor?.linkTableList?.find((t) => t.name == table.name) !== undefined;
    return (React.createElement("div", { className: styles.schemaMonitorItemCell, style: { display: "flex", justifyContent: "center" }, onClick: (e) => {
            e.stopPropagation();
            dispatch(toggleSelectedSchemaLinkTable(table));
        } },
        React.createElement("input", { type: "checkbox", checked: isInLinkTableList, onChange: () => undefined })));
};
const SchemaMonitorTableName = ({ monitorId, table }) => {
    const dispatch = useDispatch();
    const monitor = useSelector((state) => getSchemaMonitor(state, monitorId));
    const isSelected = monitor?.selectedTable?.name == table.name;
    return (React.createElement(Tooltip, { trigger: ["hover"], overlay: React.createElement(React.Fragment, null, table.name) },
        React.createElement("div", { className: styles.schemaMonitorItemCell, onClick: () => {
                if (isSelected) {
                    dispatch(selectTableInSelectedMonitor(undefined));
                }
                else {
                    dispatch(selectTableInSelectedMonitor(table));
                }
            } }, table.name)));
};
const SchemaMonitorTableOutcome = ({ monitorId, table }) => {
    const dispatch = useDispatch();
    const monitor = useSelector((state) => getSchemaMonitor(state, monitorId));
    const isInLinkTableList = monitor?.linkTableList?.find((t) => t.name == table.name) !== undefined;
    const linkTableOutcomes = monitor?.linkTableOutcomes ?? {};
    const outcomeOptions = table?.columns
        ?.map((c) => ({
        ...c,
        label: c?.name,
        value: c?.name
    }))
        .sort(getStringComparator((x) => x.name));
    const selectedOutcome = linkTableOutcomes?.[table?.name ?? ""];
    const divId = `sm-outcome-${monitorId}-${table.name}`;
    return (React.createElement("div", { id: divId, className: styles.schemaMonitorItemCell, style: { padding: "1px" }, onClick: (e) => e.stopPropagation() },
        React.createElement(Select, { key: Number(selectedOutcome === undefined), styles: {
                menuList: (provided, _) => ({
                    ...provided,
                    maxHeight: "200px"
                }),
                menuPortal: (provided, _) => {
                    const element = document.getElementById(divId);
                    const portalTarget = document.getElementById("schema-monitor-modal");
                    if (element === null || portalTarget === null) {
                        return provided;
                    }
                    const rect = element.getBoundingClientRect();
                    const parentRect = portalTarget.getBoundingClientRect();
                    return {
                        ...provided,
                        left: rect.left - parentRect.left + portalTarget.offsetLeft,
                        top: rect.bottom - parentRect.bottom - 12,
                        width: rect.width
                    };
                }
            }, options: outcomeOptions, value: selectedOutcome, onChange: selectOnChangeWrapperIfPresent((val) => {
                dispatch(updateSchemaLinkTableOutcome(monitorId, table, [{ value: val, label: val }]));
            }), menuPlacement: "top", menuPosition: "fixed", menuPortalTarget: document.getElementById("schema-monitor-modal")?.parentElement, isDisabled: !isInLinkTableList, menuShouldScrollIntoView: true })));
};
const SchemaMonitorTableTransform = ({ monitorId, table }) => {
    const dispatch = useDispatch();
    const monitor = useSelector((state) => getSchemaMonitor(state, monitorId));
    const isInLinkTableList = monitor?.linkTableList?.find((t) => t.name == table.name) !== undefined;
    const linkTableTransformations = useSelector((state) => getTransformRecommendations(state, monitorId, table));
    const selectedTransformation = monitor?.selectedTransformations?.[table.name];
    const linkTableTransformationOptions = useSelector((state) => linkTableTransformations?.map((id) => ({
        value: id,
        label: `(${id}) ${getDataset(state, id)?.name ?? ""}`
    })) ?? []);
    const selectedTransformationDatasetName = useSelector((state) => getDataset(state, selectedTransformation)?.name);
    const selectedTranformationOption = selectedTransformation === undefined || selectedTransformationDatasetName === undefined
        ? undefined
        : {
            value: selectedTransformation,
            label: `(${selectedTransformation}) ${selectedTransformationDatasetName}`
        };
    const divId = `sm-transform-${monitorId}-${table.name}`;
    return (React.createElement("div", { id: divId, className: styles.schemaMonitorItemCell, onClick: (e) => e.stopPropagation() },
        React.createElement(Select, { key: Number(selectedTranformationOption === undefined), styles: {
                menuList: (provided, _) => ({
                    ...provided,
                    maxHeight: "200px"
                }),
                menuPortal: (provided, _) => {
                    const element = document.getElementById(divId);
                    const portalTarget = document.getElementById("schema-monitor-modal");
                    if (element === null || portalTarget === null) {
                        return provided;
                    }
                    const rect = element.getBoundingClientRect();
                    const parentRect = portalTarget.getBoundingClientRect();
                    return {
                        ...provided,
                        left: rect.left - parentRect.left + portalTarget.offsetLeft,
                        top: rect.bottom - parentRect.bottom - 12,
                        width: rect.width
                    };
                }
            }, options: linkTableTransformationOptions, value: selectedTranformationOption, onChange: selectOnChangeWrapper((val) => dispatch(updateSchemaMonitorLinkTableTransformation(monitorId, table, val))), menuPosition: "fixed", menuPlacement: "top", menuPortalTarget: document.getElementById("schema-monitor-modal"), isDisabled: !isInLinkTableList })));
};
const SchemaMonitorTableColumns = ({ table }) => {
    return (React.createElement("div", { className: styles.schemaMonitorItemCell, title: table.columns?.length?.toFixed(0) }, table?.columns?.length ?? 0));
};
const SchemaMonitorTables = ({ monitorId }) => {
    const dispatch = useDispatch();
    const monitor = useSelector((state) => getSchemaMonitor(state, monitorId));
    const tables = useSelector((state) => getSchemaTables(state, monitorId));
    const selectedTable = useSelector((state) => getSchemaMonitorSelectedTable(state, monitorId));
    const linkTableList = useSelector((state) => getSchemaMonitorLinkTableList(state, monitorId));
    const safeImportMode = useSelector((state) => getIsSafeImportMode(state, monitorId));
    const firstSize = tables[0]?.columns?.length ?? 0;
    const columnRange = tables
        .map((t) => t.columns.length)
        .reduce((range, length) => {
        if (length < range[0]) {
            range[0] = length;
        }
        if (length > range[1]) {
            range[1] = length;
        }
        return range;
    }, [firstSize, firstSize]);
    const [tableFilter, updateTableFilter] = useState();
    const [columnFilters, updateColumnFilters] = useState(columnRange);
    const retTableFilter = (tableFilter ?? "").toLowerCase();
    const retFieldFilter = retTableFilter;
    const retTables = (tables || [])
        .filter((table) => {
        let shouldShowTable = !retTableFilter;
        let shouldShowField = !retFieldFilter;
        if (retTableFilter && table.name?.toLowerCase().includes(retTableFilter)) {
            shouldShowTable = true;
        }
        if (retFieldFilter && table.columns.find((c) => c.name?.toLowerCase().includes(retFieldFilter))) {
            shouldShowField = true;
        }
        const columnCount = table?.columns?.length ?? 0;
        if (columnCount < columnFilters?.[0] || columnCount > columnFilters?.[1]) {
            return false;
        }
        return shouldShowTable || shouldShowField;
    })
        .sort(getStringComparator((x) => x.name));
    const isAllSelected = retTables &&
        !retTables?.find((currTable) => {
            const currTableName = currTable?.name;
            const selectedTable = linkTableList.find((t) => t?.name === currTableName);
            return !selectedTable;
        });
    const datasetColumns = [
        { label: "", dataKey: "checked", width: 40 },
        {
            label: `${retTables?.length}${retTables?.length === tables?.length ? "" : ` of ${tables?.length}`} Table${tables?.length === 1 ? "" : "s"}`,
            dataKey: "table",
            width: 490,
            flexGrow: 1
        },
        { label: "Outcome to Evaluate (Optional)", dataKey: "outcome", width: 220 },
        {
            label: "Apply Transformations from Another Dataset (Optional)",
            dataKey: "transform",
            width: 350
        },
        { label: "Columns", dataKey: "columns", width: 70 }
    ];
    const rowHeight = 50;
    const tableHeight = retTables?.length * rowHeight + 50;
    const throttledUpdateTableFilter = debounce(250, updateTableFilter);
    const throttledUpdateColumnFilters = debounce(50, updateColumnFilters);
    return (React.createElement("div", { className: styles.tableListWrapper, style: selectedTable === undefined ? { width: "100%" } : {} },
        React.createElement("div", { className: styles.tableListTitle, style: { justifyContent: "space-between", alignItems: "center" } },
            React.createElement(SearchInput, { value: tableFilter, onChange: throttledUpdateTableFilter }),
            React.createElement("div", { className: styles.columnSliderWrapper, style: { marginLeft: "5px" } },
                React.createElement("div", { className: styles.columnSliderText }, "Show tables with a number of columns between"),
                React.createElement("div", { className: styles.columnSlider },
                    React.createElement(RangeSlider, { value: columnFilters, onChange: throttledUpdateColumnFilters, min: columnRange[0], max: columnRange[1], isMiniOptimize: true, sliderStyle: { marginLeft: "5px", marginRight: "10px" } }))),
            React.createElement("div", { style: { display: "flex", alignItems: "center" } },
                React.createElement("div", null, "Enable Safe Import Mode"),
                React.createElement(Tooltip, { overlay: "Safe import mode disables advanced automatic data introspection. This can be helpful if there are problematic columns or values in the data that need to be changed an removed before additional analysis can be performed." }),
                React.createElement(LabeledToggle, { style: { display: "block", marginLeft: "5px" }, checked: safeImportMode ?? false, onClick: () => {
                        dispatch(toggleSafeModeImport(!safeImportMode));
                    }, isAibleColors: true }))),
        React.createElement("div", { className: styles.tableList },
            React.createElement(AutoSizer, null, ({ width, height }) => (React.createElement(Table, { width: width, height: Math.min(height, tableHeight), headerHeight: 50, rowHeight: rowHeight, rowCount: retTables?.length, rowGetter: ({ index }) => retTables[index], rowClassName: ({ index }) => styles.schemaMonitorRow +
                    (index < 0 ? " " + styles.isHeader : "") +
                    (monitor?.selectedTable?.name !== undefined && monitor?.selectedTable?.name == retTables[index]?.name
                        ? " " + styles.active
                        : ""), className: styles.schemaMonitorTable, headerClassName: styles.schemaMonitorHeaderRow }, datasetColumns.map((column, index) => {
                if (column.dataKey == "checked") {
                    return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.schemaMonitorItem, headerClassName: styles.schemaMonitorItem, headerRenderer: () => {
                            return (React.createElement(Tooltip, { overlay: isAllSelected ? "Deselect All" : "Select All", trigger: ["hover"] },
                                React.createElement("div", { className: styles.schemaMonitorItemCell, style: { display: "flex", justifyContent: "center" }, onClick: (e) => {
                                        e?.stopPropagation();
                                        const limitedSelect = [...retTables];
                                        dispatch(updateSelectedSchemaLinkTableList(isAllSelected ? [] : limitedSelect));
                                    } },
                                    React.createElement("input", { type: "checkbox", checked: isAllSelected, onChange: () => undefined }))));
                        }, cellRenderer: ({ rowData }) => {
                            return React.createElement(SchemaMonitorTableChecked, { monitorId: monitorId, table: rowData });
                        } }));
                }
                else if (column.dataKey == "table") {
                    return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.schemaMonitorItem, headerClassName: styles.schemaMonitorItem, cellRenderer: ({ rowData }) => {
                            return React.createElement(SchemaMonitorTableName, { monitorId: monitorId, table: rowData });
                        } }));
                }
                else if (column.dataKey == "outcome") {
                    return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.schemaMonitorItem, headerClassName: styles.schemaMonitorItem, headerRenderer: () => {
                            return (React.createElement("div", { className: styles.tableListOutcomeColumn },
                                React.createElement("div", null, "Outcome to Evaluate (Optional)"),
                                React.createElement("div", null,
                                    React.createElement("a", { onClick: () => dispatch(generateOutcomeRecommendations(monitorId)) }, "Add"),
                                    React.createElement("span", null, " / "),
                                    React.createElement("a", { onClick: () => dispatch(removeOutcomeRecommendations(monitorId)) }, "Remove"),
                                    React.createElement("span", null, " auto-suggestions"))));
                        }, cellRenderer: ({ rowData }) => {
                            return React.createElement(SchemaMonitorTableOutcome, { monitorId: monitorId, table: rowData });
                        } }));
                }
                else if (column.dataKey == "transform") {
                    return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.schemaMonitorItem, headerClassName: styles.schemaMonitorItem, headerRenderer: () => {
                            return (React.createElement("div", { className: styles.tableListTransformColumn },
                                React.createElement("div", null, "Apply Transformations from Another Dataset (Optional)"),
                                React.createElement("div", null,
                                    React.createElement("a", { onClick: () => dispatch(generateTransformRecommendations(monitorId)) }, "Add"),
                                    React.createElement("span", null, " / "),
                                    React.createElement("a", { onClick: () => dispatch(resetTransformRecommendations(monitorId)) }, "Remove"),
                                    React.createElement("span", null, " auto-suggestions"))));
                        }, cellRenderer: ({ rowData }) => {
                            return React.createElement(SchemaMonitorTableTransform, { monitorId: monitorId, table: rowData });
                        } }));
                }
                else if (column.dataKey == "columns") {
                    return (React.createElement(Column, { key: index, label: column.label, dataKey: column.dataKey, width: column.width, className: styles.schemaMonitorItem, headerClassName: styles.schemaMonitorItem, cellRenderer: ({ rowData }) => {
                            return React.createElement(SchemaMonitorTableColumns, { monitorId: monitorId, table: rowData });
                        } }));
                }
            })))))));
};
const SchemaMonitorTableDetails = ({ monitorId, noClick }) => {
    const selectedTable = useSelector((state) => getSchemaMonitorSelectedTable(state, monitorId));
    const [columnFilter, updateColumnFilter] = useState("");
    const retColumns = (selectedTable?.columns ?? [])
        .filter((column) => column.name.toLowerCase().includes(columnFilter.toLowerCase()))
        .sort(getStringComparator((x) => x.name));
    if (!selectedTable) {
        return React.createElement(React.Fragment, null);
    }
    const throttledUpdateColumnFilter = debounce(50, updateColumnFilter);
    return (React.createElement("div", { className: styles.tableListWrapper + " " + styles.isDetails + " " + (noClick ? styles.noClick : "") },
        React.createElement("div", { className: styles.tableListTitle, style: { marginBottom: "15px", alignItems: "center" } },
            React.createElement("div", { className: "whitespace-nowrap text-ellipsis overflow-hidden mr-[10px] w-[140px]" }, selectedTable.name),
            React.createElement(SearchInput, { value: columnFilter, onChange: throttledUpdateColumnFilter, style: { flex: "1", display: "flex", width: "175px" }, inputClass: "flex-1 w-[unset]" })),
        React.createElement("div", { className: styles.tableList },
            React.createElement(AutoSizer, null, ({ width, height }) => (React.createElement(Table, { width: width, height: height, headerHeight: 50, rowHeight: 50, rowCount: retColumns?.length, rowGetter: ({ index }) => retColumns[index], rowClassName: ({ index }) => styles.schemaMonitorRow + (index < 0 ? " " + styles.isHeader : ""), className: styles.schemaMonitorTable, headerClassName: styles.schemaMonitorHeaderRow },
                React.createElement(Column, { label: "Column", dataKey: "name", width: 200, className: styles.schemaMonitorItem, headerClassName: styles.schemaMonitorItem, cellRenderer: ({ cellData }) => {
                        return (React.createElement(Tooltip, { trigger: ["hover"], overlay: React.createElement(React.Fragment, null, cellData) },
                            React.createElement("div", { className: styles.tableListName }, cellData)));
                    } }),
                React.createElement(Column, { label: "Type", dataKey: "type", width: 100, className: styles.schemaMonitorItem, headerClassName: styles.schemaMonitorItem, cellRenderer: ({ cellData }) => {
                        return React.createElement("div", { className: styles.tableListType }, cellData);
                    } })))))));
};
export default withRouter(SchemaMonitorModal);
